import React, { type FC, useState, useMemo } from 'react';
import { Form, Formik, type FormikHelpers } from 'formik';
import {
  UiGrid,
  UiText,
  type UiHStackProps,
  UiHStack,
  UiSwitch,
  uiStyles,
  UiStack,
  UiButton, UiIconCaretLeft, UiIconCaretRight,
} from '@/lib/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Yup from 'yup';
import BaseFormFieldGroup from '@/base/Form/FieldGroup';
import BaseFormInputField from '@/base/Form/InputField';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import BaseTransitionSlideLeft from '@/base/Transition/SlideLeft';
import BaseFormSelectField, { type Option } from '@/base/Form/SelectField';
import { saveAddresses, type AddressSaveRequestItem, addressQueryKey, type BothAddressResponse, type Address, addressQueryKeyBoth, RegistrationStrategyType, type AddressSaveRequest } from '@/api/registration';
import { useTenantApi } from '@/account/hook/useTenantApi';
import LayoutFooter from '@/registration/component/Register/Layout/Footer';
import { useRegisterSteps } from '@/registration/hook/useRegisterSteps';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { type ApiResponse } from '@/api/tenantClient';

import countries from '@/app/countries.json';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useRegisterUpsellSteps } from '@/registration/hook/useRegisterUpsellSteps';
import BaseThemeButton from '@/base/ThemeButton';
import BrandFooter from '@/registration/component/Register/Layout/BrandFooter';

const countryOptions = countries.map((countryName) => ({ value: countryName, label: countryName }));

export interface AddressFormProps extends UiHStackProps {
  existingAddresses: BothAddressResponse
  isUpsell?: boolean
}

export interface AddressFormData {
  firstName: string
  lastName: string
  billingStreet: string
  billingCity: string
  billingCountry: string
  billingState: string
  billingPostcode: string
  billingCompanyName: string
  hasShipping: boolean
  shippingStreet: string
  shippingCity: string
  shippingCountry: string
  shippingState: string
  shippingPostcode: string
}

const AddressForm: FC<AddressFormProps> = ({ existingAddresses, isUpsell }) => {
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const { eventId } = useRegisterRoute();
  const registrationQuery = useRegistrationQuery(eventId);
  const registration = registrationQuery?.data;
  const queryClient = useQueryClient();
  const { createTenantApiRequest } = useTenantApi();
  const paymentFirst = registration?.registrationStrategy === RegistrationStrategyType.PAYMENT_FIRST;

  const { toNextStep: defaultToNextStep, toPreviousStep: defaultToPreviousStep } = useRegisterSteps();
  const { toNextStep: upsellToNextStep, toPreviousStep: upsellToPreviousStep } = useRegisterUpsellSteps();

  const toNextStep = isUpsell ? upsellToNextStep : defaultToNextStep;
  const toPreviousStep = isUpsell ? upsellToPreviousStep : defaultToPreviousStep;

  const formSchema = Yup.object().shape({
    firstName: paymentFirst ? Yup.string().required('First name is required.') : Yup.string(),
    lastName: paymentFirst ? Yup.string().required('Last name is required.') : Yup.string(),
    billingCompanyName: Yup.string(),
    billingStreet: Yup.string()
      .required('Street address is required.'),
    billingCity: Yup.string()
      .required('City is required.'),
    billingCountry: Yup.string().required('Country is required.'),
    billingState: Yup.string()
      .required('State is required.'),
    billingPostcode: Yup.string()
      .required('Postcode is required.'),
    hasShipping: Yup.boolean(),
    shippingStreet: Yup.string()
      .when('hasShipping', {
        is: true,
        then: (schema) => schema.required('Shipping street address is required.')
      }),
    shippingCity: Yup.string()
      .when('hasShipping', {
        is: true,
        then: (schema) => schema.required('Shipping city is required.')
      }),
    shippingCountry: Yup.string()
      .when('hasShipping', {
        is: true,
        then: (schema) => schema.required('Country is required.')
      }),
    shippingState: Yup.string()
      .when('hasShipping', {
        is: true,
        then: (schema) => schema.required('Shipping state is required.')
      }),
    shippingPostcode: Yup.string()
      .when('hasShipping', {
        is: true,
        then: (schema) => schema.required('Shipping postcode is required.')
      })
  });

  const { mutateAsync, isLoading } = useMutation<ApiResponse<Address[]>, Error, AddressSaveRequest>({
    mutationFn: async (saveRequest: AddressSaveRequest) => {
      return await saveAddresses(createTenantApiRequest)(saveRequest);
    },
    onSuccess: async (data: ApiResponse<Address[]>) => {
      if (data?.errors?.length && data?.errors?.length > 0) {
        setSaveErrors(data.errors);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: [addressQueryKey] });
      await queryClient.invalidateQueries({ queryKey: [addressQueryKeyBoth] });
      toNextStep();
    },
    onError: (error) => {
      setSaveErrors([error.message ?? 'Failed to save the address.']);
    }
  });

  const submitForm = async (values: AddressFormData) => {
    const billingAddress: AddressSaveRequestItem = {
      id: existingAddresses?.billingAddress?.id,
      address: values.billingStreet,
      suburb: values.billingCity,
      country: values.billingCountry,
      state: values.billingState,
      postCode: values.billingPostcode,
      companyName: values.billingCompanyName,
      registrationId: registration!.id,
      type: 'billing'
    };

    const shippingAddress: AddressSaveRequestItem = values.hasShipping ? {
      id: existingAddresses?.shippingAddress?.id,
      address: values.shippingStreet,
      suburb: values.shippingCity,
      country: values.shippingCountry,
      state: values.shippingState,
      postCode: values.shippingPostcode,
      registrationId: registration!.id,
      type: 'shipping'
    } : { ...billingAddress, type: 'shipping', id: existingAddresses?.shippingAddress?.id };

    const { firstName, lastName } = values;

    await mutateAsync({ addresses: [billingAddress, shippingAddress], firstName, lastName, registrationId: registrationQuery?.data?.id });
  };

  const areAddressesIdentical = (billingAddress: Address, shippingAddress: Address) => {
    return (
      billingAddress?.address === shippingAddress?.address &&
      billingAddress?.suburb === shippingAddress?.suburb &&
      billingAddress?.country === shippingAddress?.country &&
      billingAddress?.state === shippingAddress?.state &&
      billingAddress?.postCode === shippingAddress?.postCode
    );
  };

  return (
    <Formik
      initialValues={{
        firstName: registrationQuery?.data?.firstName === 'N/A' ? '' : registrationQuery?.data?.firstName ?? '',
        lastName: registrationQuery?.data?.lastName === 'N/A' ? '' : registrationQuery?.data?.lastName ?? '',
        billingStreet: existingAddresses?.billingAddress?.address ?? '',
        billingCity: existingAddresses?.billingAddress?.suburb ?? '',
        billingCountry: existingAddresses?.billingAddress?.country ?? '',
        billingState: existingAddresses?.billingAddress?.state ?? '',
        billingPostcode: existingAddresses?.billingAddress?.postCode ?? '',
        billingCompanyName: existingAddresses?.billingAddress?.companyName ?? '',
        hasShipping: !areAddressesIdentical(existingAddresses?.billingAddress, existingAddresses?.shippingAddress),
        shippingStreet: existingAddresses?.shippingAddress?.address ?? '',
        shippingCity: existingAddresses?.shippingAddress?.suburb ?? '',
        shippingCountry: existingAddresses?.shippingAddress?.country ?? '',
        shippingState: existingAddresses?.shippingAddress?.state ?? '',
        shippingPostcode: existingAddresses?.shippingAddress?.postCode ?? ''
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={formSchema}
      onSubmit={async (
        values: AddressFormData,
        { setSubmitting }: FormikHelpers<AddressFormData>
      ) => {
        setSubmitting(true);
        await submitForm(values);
        setSubmitting(false);
      }}
    >
      {({ values, setFieldValue }) => {
        return (
          <Form>
            <BaseTransitionSlideLeft>
              {saveErrors.length > 0 && (
                <UiStack spacing={4} flexGrow={1} py={4}>
                  {saveErrors.map((error) => {
                    return (
                      <BaseMessageBarError key={error}>
                        {error}
                      </BaseMessageBarError>
                    );
                  })}
                </UiStack>
              )}
              <UiStack flexGrow={1}>
                <BaseTransitionSlideLeft>
                  <UiGrid
                    templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
                    gap={12}
                    alignItems={'stretch'}
                    flexGrow={1}
                  >
                    <BaseFormFieldGroup>
                      <UiText variant={'title'}>Billing address</UiText>
                      {paymentFirst && (
                        <>
                          <BaseFormInputField
                            name={'firstName'}
                            label={'First name'}
                            layout={'stack'}
                          />
                          <BaseFormInputField
                            name={'lastName'}
                            label={'Last name'}
                            layout={'stack'}
                          />
                        </>
                      )}
                      <BaseFormInputField
                        name={'billingCompanyName'}
                        label={'Company / Business name'}
                        layout={'stack'}
                        isRequired={false}
                      />
                      <BaseFormInputField
                        name={'billingStreet'}
                        label={'Street address'}
                        layout={'stack'}
                      />
                      <BaseFormInputField
                        name={'billingCity'}
                        label={'Town/Suburb/City'}
                        layout={'stack'}
                      />
                      <BaseFormInputField
                        name={'billingState'}
                        label={'State'}
                        layout={'stack'}
                      />
                      <BaseFormInputField
                        name={'billingPostcode'}
                        label={'Postcode'}
                        layout={'stack'}
                      />
                      <BaseFormSelectField
                        name={'billingCountry'}
                        label={'Country'}
                        options={countryOptions}
                        layout={'stack'}
                      />
                    </BaseFormFieldGroup>
                    <BaseFormFieldGroup>
                      <UiHStack>
                        <UiSwitch
                          colorScheme={'primary'}
                          onChange={() => { void setFieldValue('hasShipping', !values.hasShipping); }}
                          isChecked={values.hasShipping}
                          size={'lg'}
                        />
                        <UiText variant={'title'}>Shipping address</UiText>
                      </UiHStack>
                      {values.hasShipping ? (
                        <>
                          <BaseFormInputField
                            name={'shippingStreet'}
                            label={'Street address'}
                            layout={'stack'}
                          />
                          <BaseFormInputField
                            name={'shippingCity'}
                            label={'Town/Suburb/City'}
                            layout={'stack'}
                          />
                          <BaseFormInputField
                            name={'shippingState'}
                            label={'State'}
                            layout={'stack'}
                          />
                          <BaseFormInputField
                            name={'shippingPostcode'}
                            label={'Postcode'}
                            layout={'stack'}
                          />
                          <BaseFormSelectField
                            name={'shippingCountry'}
                            label={'Country'}
                            options={countryOptions}
                            layout={'stack'}
                          />
                        </>
                      ) : (
                        <BaseMessageBarInfo borderRadius={uiStyles.borderRadius}>Same as billing</BaseMessageBarInfo>
                      )}
                    </BaseFormFieldGroup>
                  </UiGrid>
                </BaseTransitionSlideLeft>
              </UiStack>
            </BaseTransitionSlideLeft>
            <UiStack height={'450px'} />
            <LayoutFooter>
              <UiHStack justifyContent={'space-between'} flexGrow={1}>
                {/*<UiButton*/}
                {/*  size={'lg'}*/}
                {/*  colorScheme={'primary'}*/}
                {/*  onClick={toPreviousStep}*/}
                {/*  variant={'ghost'}*/}
                {/*  fontSize={'lg'}*/}
                {/*  leftIcon={(*/}
                {/*    <UiIconCaretLeft color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
                {/*  )}*/}
                {/*>*/}
                {/*  Previous*/}
                {/*</UiButton>*/}
                {/*<UiButton*/}
                {/*  size={'lg'}*/}
                {/*  colorScheme={'primary'}*/}
                {/*  type={'submit'}*/}
                {/*  isLoading={isLoading}*/}
                {/*  variant={'ghost'}*/}
                {/*  fontSize={'lg'}*/}
                {/*  rightIcon={(*/}
                {/*    <UiIconCaretRight color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
                {/*  )}*/}
                {/*>*/}
                {/*  Go to pay*/}
                {/*</UiButton>*/}

                <BaseThemeButton
                  theme={'gray'}
                  onClick={toPreviousStep}
                  leftIcon={(
                    <UiIconCaretLeft color={'primary.800'} size={'lg'} weight={'bold'}/>
                  )}
                >
                  Prev
                </BaseThemeButton>
                <BrandFooter/>
                <BaseThemeButton
                  theme={'primary'}
                  type={'submit'}
                  isLoading={isLoading}
                  rightIcon={(
                    <UiIconCaretRight color={'primary.800'} size={'lg'} weight={'bold'}/>
                  )}
                >
                  Next
                </BaseThemeButton>
              </UiHStack>
            </LayoutFooter>
          </Form>
        );
      }}
    </Formik>
  );
};

export default AddressForm;
